/**
 * dialogue.ts — RPG-style typewriter dialogue box with beat pauses and advance queue (Phase 43).
 *
 * Renders a semi-transparent box at the bottom of the canvas. Text types
 * character-by-character at 40ms/char. *(beat)* markers produce a 400ms pause
 * and are never rendered. A blinking ▼ indicator appears when waiting for
 * player to advance. New lines are queued and never interrupt an in-progress line.
 *
 * Module-level singleton — no React, no classes.
 */

// ---------------------------------------------------------------------------
// Constants (all locked in 43-CONTEXT.md)
// ---------------------------------------------------------------------------

const CHAR_INTERVAL_MS = 40;    // DIAL-01: 40ms per character
const BEAT_PAUSE_MS = 400;      // DIAL-01: 400ms pause on *(beat)*
const BLINK_INTERVAL_MS = 500;  // DIAL-03: blink every 500ms
const AUTO_ADVANCE_MS = 3000;   // auto-advance to next queued line after 3s idle
const BEAT_RE = /\*\(beat\)\*/g;

// Dialogue box visual specs (locked in CONTEXT.md)
const BOX_MARGIN_LEFT = 80;    // leaves space for Phase 44 character sprite
const BOX_HEIGHT_FULL = 100;
const BOX_RADIUS = 8;

let boxHeight = BOX_HEIGHT_FULL;
const BOX_BG = 'rgba(10, 10, 10, 0.88)';
const BOX_BORDER = 'rgba(200, 200, 200, 0.3)';
const BOX_BORDER_WIDTH = 0.5;
const TEXT_COLOR = '#e0e0e0';
const TEXT_FONT = '13px "Courier New", monospace';
const BLINK_CHAR = '\u25BC'; // down-pointing triangle ▼
const LINE_HEIGHT = 18;       // px between wrapped lines (13px font)
const TEXT_PAD_X = 12;        // left padding inside box
const TEXT_PAD_Y = 14;        // top padding inside box

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface DialogueState {
  queue: string[];
  segments: string[];        // current line split on *(beat)*
  segmentIndex: number;
  charIndex: number;         // chars rendered in current segment
  charTimer: number;         // ms accumulator for char typing
  beatTimer: number;         // ms remaining in beat pause (0 = not pausing)
  waitingForAdvance: boolean;
  autoAdvanceTimer: number;  // ms elapsed while waitingForAdvance — auto-advances at AUTO_ADVANCE_MS
  blinkOn: boolean;
  blinkTimer: number;        // ms accumulator for blink toggle
  active: boolean;           // true when any line is queued or in progress
}

const state: DialogueState = {
  queue: [],
  segments: [],
  segmentIndex: 0,
  charIndex: 0,
  charTimer: 0,
  beatTimer: 0,
  waitingForAdvance: false,
  autoAdvanceTimer: 0,
  blinkOn: true,
  blinkTimer: 0,
  active: false,
};

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function startNextLine(): void {
  if (state.queue.length === 0) {
    state.active = false;
    return;
  }
  const line = state.queue.shift() as string;
  // Split on beat markers, filter empty segments (e.g. leading/trailing markers)
  const raw = line.split(BEAT_RE);
  state.segments = raw.filter((s) => s.length > 0);

  // Pure beat line (e.g. "*(beat)*" alone) — apply pause then skip to next line.
  // segments would be empty so we guard here instead of crashing in updateDialogue.
  if (state.segments.length === 0) {
    state.segmentIndex = 0;
    state.charIndex = 0;
    state.charTimer = 0;
    state.beatTimer = BEAT_PAUSE_MS;
    state.waitingForAdvance = false;
    state.blinkOn = true;
    state.blinkTimer = 0;
    state.active = true;
    return;
  }

  state.segmentIndex = 0;
  state.charIndex = 0;
  state.charTimer = 0;
  state.beatTimer = 0;
  state.waitingForAdvance = false;
  state.autoAdvanceTimer = 0;
  state.blinkOn = true;
  state.blinkTimer = 0;
  state.active = true;
}

// ---------------------------------------------------------------------------
// Exported API
// ---------------------------------------------------------------------------

/**
 * Queue a dialogue line. Never interrupts the line currently being typed.
 * If no line is active, starts typing immediately.
 */
export function queueLine(text: string): void {
  state.queue.push(text);
  if (!state.active) {
    startNextLine();
  }
}

/**
 * Called each frame with delta-time in seconds.
 * Returns true if a new character was typed this frame (caller can play dialogueTick).
 */
export function updateDialogue(dt: number): boolean {
  if (!state.active) return false;

  const ms = dt * 1000;

  // When waiting for player to advance — drive blink + auto-advance timer
  if (state.waitingForAdvance) {
    state.blinkTimer += ms;
    if (state.blinkTimer >= BLINK_INTERVAL_MS) {
      state.blinkOn = !state.blinkOn;
      state.blinkTimer -= BLINK_INTERVAL_MS;
    }
    state.autoAdvanceTimer += ms;
    if (state.autoAdvanceTimer >= AUTO_ADVANCE_MS) {
      state.autoAdvanceTimer = 0;
      startNextLine();
    }
    return false;
  }

  // During a beat pause — drain timer
  if (state.beatTimer > 0) {
    state.beatTimer -= ms;
    if (state.beatTimer < 0) state.beatTimer = 0;
    return false;
  }

  // Pure beat line: segments is empty, beat timer already drained — advance to next line
  if (state.segments.length === 0) {
    startNextLine();
    return false;
  }

  // Advance charTimer and type characters
  state.charTimer += ms;
  let typed = false;

  while (state.charTimer >= CHAR_INTERVAL_MS) {
    state.charTimer -= CHAR_INTERVAL_MS;
    const seg = state.segments[state.segmentIndex];

    // Check exhaustion BEFORE incrementing so we don't fire typed=true for a
    // character that doesn't exist (segment boundary / end-of-line).
    if (state.charIndex >= seg.length) {
      // Current segment exhausted — transition without typing a character
      if (state.segmentIndex < state.segments.length - 1) {
        state.segmentIndex++;
        state.charIndex = 0;
        state.beatTimer = BEAT_PAUSE_MS;
      } else {
        state.waitingForAdvance = true;
        state.blinkOn = true;
        state.blinkTimer = 0;
      }
      break;
    }

    state.charIndex++;
    typed = true;

    if (state.charIndex >= seg.length) {
      // Just reached end of segment — transition after this character
      if (state.segmentIndex < state.segments.length - 1) {
        state.segmentIndex++;
        state.charIndex = 0;
        state.beatTimer = BEAT_PAUSE_MS;
      } else {
        state.waitingForAdvance = true;
        state.blinkOn = true;
        state.blinkTimer = 0;
      }
      break;
    }
  }

  return typed;
}

/**
 * Skip to end of current line, or advance queue to next line.
 * DIAL-04: first press skips typing; second press advances queue.
 */
export function advanceDialogue(): void {
  if (!state.active) return;

  if (state.waitingForAdvance) {
    startNextLine();
    return;
  }

  // Guard against empty segments (pure beat line mid-pause — nothing to skip)
  if (state.segments.length === 0) return;

  // Skip to end of current line immediately
  state.segmentIndex = state.segments.length - 1;
  state.charIndex = state.segments[state.segmentIndex].length;
  state.beatTimer = 0;
  state.waitingForAdvance = true;
  state.blinkOn = true;
  state.blinkTimer = 0;
}

/**
 * Render the dialogue box and typed text onto the canvas.
 * Call after all gameplay rendering, before HUD.
 */
export function drawDialogue(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number
): void {
  if (boxHeight === 0) return;
  if (!state.active) return;

  const x = BOX_MARGIN_LEFT;
  const y = cssH - 140;
  const w = cssW - 100;
  const h = boxHeight;

  // Background
  ctx.fillStyle = BOX_BG;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, BOX_RADIUS);
  } else {
    ctx.rect(x, y, w, h);
  }
  ctx.fill();

  // Border
  ctx.strokeStyle = BOX_BORDER;
  ctx.lineWidth = BOX_BORDER_WIDTH;
  ctx.stroke();

  // Build visible text: completed segments fully + current segment up to charIndex
  let visibleText = '';
  for (let i = 0; i < state.segmentIndex; i++) {
    visibleText += state.segments[i];
  }
  if (state.segments.length > 0) {
    visibleText += state.segments[state.segmentIndex].substring(0, state.charIndex);
  }

  // Draw text with word-wrap
  ctx.fillStyle = TEXT_COLOR;
  ctx.font = TEXT_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  const maxTextWidth = w - TEXT_PAD_X * 2;
  const words = visibleText.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const candidate = currentLine.length === 0 ? word : `${currentLine} ${word}`;
    if (ctx.measureText(candidate).width > maxTextWidth && currentLine.length > 0) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  }
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], x + TEXT_PAD_X, y + TEXT_PAD_Y + i * LINE_HEIGHT);
  }

  // Blinking advance indicator
  if (state.waitingForAdvance && state.blinkOn) {
    ctx.fillStyle = TEXT_COLOR;
    ctx.font = TEXT_FONT;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(BLINK_CHAR, x + w - 8, y + h - 8);
  }
}

/**
 * Reset all dialogue state — call on game exit or restart.
 */
export function resetDialogue(): void {
  state.queue = [];
  state.segments = [];
  state.segmentIndex = 0;
  state.charIndex = 0;
  state.charTimer = 0;
  state.beatTimer = 0;
  state.waitingForAdvance = false;
  state.autoAdvanceTimer = 0;
  state.blinkOn = true;
  state.blinkTimer = 0;
  state.active = false;
  boxHeight = BOX_HEIGHT_FULL;
}

/**
 * Returns true when a line is queued or currently being typed.
 */
export function isDialogueActive(): boolean {
  return state.active;
}

/**
 * Returns true when line is fully typed and waiting for player to press advance.
 * Used by GameCanvas key handler to distinguish skip-to-end vs advance-queue.
 */
export function isWaitingForAdvance(): boolean {
  return state.waitingForAdvance;
}

/**
 * Returns the number of lines currently waiting in the queue (not counting
 * the line currently being typed). Used by tickQuips() for queue-depth guard.
 */
export function getQueueLength(): number {
  return state.queue.length;
}

/**
 * Sets the dialogue box height based on safe zone use count (Phase 48).
 * uses 0 = full (100px), 1 = 66px, 2 = 33px, 3 = 0px (gone).
 * Called by safeZone.ts after each activation.
 */
export function setSafeZoneLevel(uses: number): void {
  if (uses === 0) { boxHeight = BOX_HEIGHT_FULL; return; }
  if (uses === 1) { boxHeight = 66; return; }
  if (uses === 2) { boxHeight = 33; return; }
  if (uses === 3) { boxHeight = 0; return; }
}
